import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { getVariableOnlyInputFeatureIds } from '@/tools/models/template.utilities';
import type { TFunction } from 'i18next';
import type { TemplateDerivedFeature, TemplateInput } from '@/tools/models/template.types';
import type { TemplateDependencyData } from '@/tools/models/templateDependencies.queries';
import type { InstanceOutputMappingV1 } from '@seeqdev/api/model/InstanceOutputMappingV1';
import type { BadgeVariant } from '@seeqdev/qomponents';

export type InputProgress = {
  mappedCount: number;
  totalCount: number;
};

export type OutputStatus = {
  /** Present only if this output depends on at least one required (non-variable) input. */
  required?: InputProgress;
  /** Present only if this output depends on at least one optional (variable-only) input. */
  optional?: InputProgress;
};

/**
 * Splits each output's required inputs (from `dependencyData`) into required vs. optional using
 * `getVariableOnlyInputFeatureIds`, then counts how many of each are in `mappedInputCanonicalIds`.
 *
 * An output's `required`/`optional` field is present only when that output actually depends on at least one
 * input of that kind -- an output with no optional inputs gets no `optional` field at all (no "0 of 0" badge),
 * and likewise for `required`.
 */
export function computeOutputStatuses(
  dependencyData: TemplateDependencyData,
  derivedFeatures: TemplateDerivedFeature[],
  inputFeatures: TemplateInput[],
  mappedInputCanonicalIds: Set<string>,
): Map<string, OutputStatus> {
  const variableOnlyFeatureIds = getVariableOnlyInputFeatureIds(derivedFeatures);
  const variableOnlyCanonicalIds = new Set(
    inputFeatures
      .filter((input) => input.id && input.canonicalId && variableOnlyFeatureIds.has(input.id))
      .map((input) => input.canonicalId!),
  );

  const toProgress = (inputCanonicalIds: string[]): InputProgress | undefined =>
    inputCanonicalIds.length > 0
      ? {
          mappedCount: inputCanonicalIds.filter((id) => mappedInputCanonicalIds.has(id)).length,
          totalCount: inputCanonicalIds.length,
        }
      : undefined;

  const statuses = new Map<string, OutputStatus>();
  dependencyData.outputInputDependencies.forEach((inputCanonicalIds, outputCanonicalId) => {
    const required: string[] = [];
    const optional: string[] = [];
    inputCanonicalIds.forEach((id) => (variableOnlyCanonicalIds.has(id) ? optional.push(id) : required.push(id)));

    statuses.set(outputCanonicalId, {
      required: toProgress(required),
      optional: toProgress(optional),
    });
  });

  return statuses;
}

/**
 * An output is complete once every required (non-variable) input it depends on is mapped -- an output with no
 * required inputs at all is trivially complete.
 */
export function isOutputComplete(status: OutputStatus | undefined): boolean {
  return !!status && (!status.required || status.required.mappedCount === status.required.totalCount);
}

export type OutputBadge = {
  variant: BadgeVariant;
  text: string;
};

/**
 * Computes each output's required and optional input counts from the dependency data and
 * mapped inputs.
 *
 * `required` and `optional` are omitted when the output has no inputs of that type,
 * avoiding "0 of 0" badges.
 */
export function computeOutputBadges(
  outputStatuses: Map<string, OutputStatus>,
  t: TFunction,
): Map<string, OutputBadge[]> {
  const badges = new Map<string, OutputBadge[]>();

  outputStatuses.forEach((status, outputId) => {
    const entries: OutputBadge[] = [];

    if (status.required) {
      const requiredComplete = status.required.mappedCount === status.required.totalCount;
      entries.push({
        variant: requiredComplete ? 'success' : 'error',
        text: requiredComplete
          ? t('TEMPLATES.OUTPUT_STATUS_COMPLETE')
          : t('TEMPLATES.OUTPUT_STATUS_REQUIRED', {
              mapped: status.required.mappedCount,
              total: status.required.totalCount,
            }),
      });
    }

    if (status.optional) {
      entries.push({
        variant: 'neutral',
        text: t('TEMPLATES.OUTPUT_STATUS_OPTIONAL', {
          mapped: status.optional.mappedCount,
          total: status.optional.totalCount,
        }),
      });
    }

    badges.set(outputId, entries);
  });

  return badges;
}

/**
 * Returns the feature IDs of the inputs required by the selected outputs, or by all
 * outputs if no selection is provided.
 *
 * Canonical IDs are normalized to lowercase before comparison.
 */
export function computeVisibleInputFeatureIds(
  dependencyData: TemplateDependencyData,
  selectedOutputCanonicalIds: Set<string> | undefined,
  inputFeatures: TemplateInput[],
): Set<string> {
  const idByCanonicalId = new Map(
    inputFeatures
      .filter((input) => input.id && input.canonicalId)
      .map((input) => [input.canonicalId!.toLowerCase(), input.id!]),
  );
  const normalizedSelectedOutputCanonicalIds = selectedOutputCanonicalIds
    ? new Set(Array.from(selectedOutputCanonicalIds, (id) => id.toLowerCase()))
    : undefined;

  const visibleCanonicalIds = new Set<string>();
  dependencyData.outputInputDependencies.forEach((inputCanonicalIds, outputCanonicalId) => {
    const normalizedOutputCanonicalId = outputCanonicalId.toLowerCase();
    if (
      normalizedSelectedOutputCanonicalIds &&
      !normalizedSelectedOutputCanonicalIds.has(normalizedOutputCanonicalId)
    ) {
      return;
    }
    inputCanonicalIds.forEach((id) => visibleCanonicalIds.add(id.toLowerCase()));
  });

  const visibleFeatureIds = new Set<string>();
  visibleCanonicalIds.forEach((canonicalId) => {
    const featureId = idByCanonicalId.get(canonicalId);
    if (featureId) visibleFeatureIds.add(featureId);
  });
  return visibleFeatureIds;
}

/**
 * Filters input features to those required by the selected outputs, always including the
 * instance-name feature. Returns all inputs until dependency data is available.
 */
export function filterInputFeaturesByOutputs(
  dependencyData: TemplateDependencyData | undefined,
  selectedOutputCanonicalIds: Set<string>,
  inputFeatures: TemplateInput[],
): TemplateInput[] {
  if (!dependencyData || dependencyData.outputInputDependencies.size === 0) return inputFeatures;

  const visibleFeatureIds = computeVisibleInputFeatureIds(dependencyData, selectedOutputCanonicalIds, inputFeatures);
  return inputFeatures.filter(
    (input) =>
      input.name === SeeqNames.Models.InstanceNameInputFeatureName || (input.id && visibleFeatureIds.has(input.id)),
  );
}

/**
 * Returns the selected outputs that depend on `outputCanonicalId`. An empty array means
 * the output can be safely unselected.
 *
 * Canonical IDs are normalized to lowercase before comparison to handle inconsistent
 * backend casing.
 */
export function validateOutputUnselection(
  outputCanonicalId: string,
  selectedOutputCanonicalIds: Set<string>,
  outputToOutputDependencies: Map<string, Set<string>>,
): string[] {
  const normalizedOutputCanonicalId = outputCanonicalId.toLowerCase();
  const normalizedDependencies = new Map(
    Array.from(outputToOutputDependencies, ([key, values]) => [
      key.toLowerCase(),
      new Set(Array.from(values, (value) => value.toLowerCase())),
    ]),
  );

  const blockingOutputCanonicalIds: string[] = [];
  for (const otherOutputCanonicalId of selectedOutputCanonicalIds) {
    const normalizedOtherOutputCanonicalId = otherOutputCanonicalId.toLowerCase();
    if (normalizedOtherOutputCanonicalId === normalizedOutputCanonicalId) continue;
    if (normalizedDependencies.get(normalizedOtherOutputCanonicalId)?.has(normalizedOutputCanonicalId)) {
      blockingOutputCanonicalIds.push(normalizedOtherOutputCanonicalId);
    }
  }
  return blockingOutputCanonicalIds;
}

/**
 * Canonical IDs of the outputs that `outputCanonicalId` depends on. These must be
 * selected too, since `outputCanonicalId` can't be computed without them. `outputToOutputDependencies`
 * already stores each output's complete transitive dependency set, so this is a direct lookup.
 */
export function computeRequiredDependencyOutputCanonicalIds(
  outputCanonicalId: string,
  outputToOutputDependencies: Map<string, Set<string>>,
): Set<string> {
  const normalizedOutputCanonicalId = outputCanonicalId.toLowerCase();
  const normalizedDependencies = new Map(
    Array.from(outputToOutputDependencies, ([key, values]) => [
      key.toLowerCase(),
      new Set(Array.from(values, (value) => value.toLowerCase())),
    ]),
  );
  return normalizedDependencies.get(normalizedOutputCanonicalId) ?? new Set();
}

/** Returns the canonical IDs of outputs that are currently active for an instance. */
export function computePreviouslySelectedOutputCanonicalIds(
  instanceOutputs: InstanceOutputMappingV1[],
  baseDerivedFeatures: Array<{ id?: string; canonicalId?: string }>,
): Set<string> {
  const canonicalIdByFeatureId = new Map(
    baseDerivedFeatures
      .filter((feature) => feature.id && feature.canonicalId)
      .map((feature) => [feature.id!, feature.canonicalId!.toLowerCase()]),
  );

  const previouslySelectedOutputCanonicalIds = new Set<string>();
  instanceOutputs.forEach((output) => {
    if (!output.outputId || output.outputIdInactive || !output.featureId) return;
    const canonicalId = canonicalIdByFeatureId.get(output.featureId);
    if (canonicalId) previouslySelectedOutputCanonicalIds.add(canonicalId);
  });
  return previouslySelectedOutputCanonicalIds;
}

/** Returns the item IDs of outputs that were removed by a reapply. */
export function computeRetiredOutputItemIds(
  previousOutputs: InstanceOutputMappingV1[],
  newOutputs: InstanceOutputMappingV1[],
): string[] {
  const newOutputIds = new Set(newOutputs.map((output) => output.outputId).filter(Boolean));
  return previousOutputs.map((output) => output.outputId).filter((id): id is string => !!id && !newOutputIds.has(id));
}
